import React, { useState } from 'react';
import { ValidateResult, useForm } from 'react-hook-form';
import { withRouter } from 'react-router-dom';
import zxcvbn from 'zxcvbn';
import CircularProgress from '@material-ui/core/CircularProgress';
import LinearProgress from '@material-ui/core/LinearProgress';
import InputAdornment from '@material-ui/core/InputAdornment';
import Button from '@material-ui/core/Button';
import styled from 'styled-components';
import * as qs from 'qs';
import { withStyles } from '@material-ui/core';
import {
  MAX_PASSWORD_LENGTH,
  passwordStrengthColors,
  passwordStrengthLabels,
} from '../../constants/User';
import { confirmPasswordReset } from '../../api/api';
import NotificationToast from '../notifications/NotificationToast';
import {
  FormContainer,
  StyledTextField,
} from './FormStyles';
import i18n from '../../i18n/i18n';
import {useTheme} from "../../contexts/ThemeContext";
import { useUser } from '../../contexts/UserContext';
import { getServerErrorCode } from '../../utils/MessageFormatter';

const style = (theme) => ({
  circularProgress: {
    width: 20,
    height: 20,
    marginRight: 8,
  },
  passwordStrength: {
    backgroundColor: 'grey',
    position: 'relative',
    bottom: '4px',
    borderBottomRightRadius: 4,
    borderBottomLeftRadius: 4,
    [theme.breakpoints.down('xs')]: {
      display: 'none',
    },
    '&[strength=strength0] > div': {
      backgroundColor: `${passwordStrengthColors[0]} !important`,
    },
    '&[strength=strength1] > div': {
      backgroundColor: `${passwordStrengthColors[1]} !important`,
    },
    '&[strength=strength2] > div': {
      backgroundColor: `${passwordStrengthColors[2]} !important`,
    },
    '&[strength=strength3] > div': {
      backgroundColor: `${passwordStrengthColors[3]} !important`,
    },
  },
});

const getPasswordStrength = (password = '') => {
  const score = Math.min(zxcvbn(password).score, 3);
  return {
    score,
    message: i18n.t(passwordStrengthLabels[score]),
    color: passwordStrengthColors[score],
  };
};

// No `width: 100%`: with the side margins it overflows #root, which clips it (overflow-x: hidden)
const Root = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: stretch;
`;

const Label = styled.span`
  display: block;
  margin-bottom: 35px;
`;

const ButtonsContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: flex-start;
`;

const StyledButton = styled(({ $bgColor, ...rest }) => (
  <Button
    {...rest}
  />
))`
  && {
    background-color: ${({ $bgColor }) => $bgColor || 'rgb(250,194,3)'};
    border-width: 0;
    border-radius: 10px;
    padding: 10px 20px;
    margin: 10px;
    width: 120px;
    text-transform: capitalize;
    font-size: 0.85em;

    ${({ disabled }) => disabled && `
     background-color: rgb(243, 243, 243);
     color: rgba(255,255,255,0.15)
     border-width: 0;
     border-radius: 10px;
     padding: 10px 20px;
     margin: 10px;
     width: 120px;
     text-transform: capitalize;
     font-size: 0.85em;
  `
}
`;

const StyledDiv = styled.div`
    padding-top: 20px;
    padding-bottom: 20px;
`;

const ResetPasswordForm = ({ location, history }) => {
  const [loading, setLoading] = useState(false);
  const [notification, setNotification] = useState<any>(null);
  const [showNotification, setShowNotification] = useState(false);
  const [submitButtonEnabled, setSubmitButtonEnable] = useState(true);

  const { token } = qs.parse(location.search, { ignoreQueryPrefix: true });

  const {
    register,
    handleSubmit,
    formState: { errors },
    setError,
    watch,
    getValues,
  } = useForm({ mode: 'onBlur' });

  const { theme } = useTheme();
  const { signIn } = useUser();

  // Strength and match are enforced by the field rules, which handleSubmit runs before this
  const onSubmit: any = async ({ password }) => {
    setSubmitButtonEnable(false);
    setLoading(true);

    try {
      const response = await confirmPasswordReset(token, password);
      if (response && response.token) {
        // Not awaited: the password is already changed and every other session revoked, and the
        // authenticate promise never settles when the socket ack is lost, so it must not block the redirect
        signIn({ token: response.token }).catch(() => {
          // eslint-disable-next-line no-console
          console.error('[auth] could not adopt the password reset token');
        });
      }
      setLoading(false);
      setNotification({
        severity: 'success',
        title: i18n.t('success'),
        message: i18n.t('reset_password_successfully'),
      });
      setShowNotification(true);
      setTimeout(() => history.push('/'), 5000);
    } catch (err: any) {
      setSubmitButtonEnable(true);
      setLoading(false);
      // Read once: on the fetch transport it consumes the response body
      switch (await getServerErrorCode(err)) {
        case 'INVALID_TOKEN':
          setNotification({
            severity: 'error',
            title: i18n.t('error'),
            message: i18n.t('no_password_reset_record_found'),
          });
          setShowNotification(true);
          return;

        case 'EXPIRED_TOKEN':
          setNotification({
            severity: 'error',
            title: i18n.t('error'),
            message: i18n.t('max_time_reset_exceeded'),
          });
          setShowNotification(true);
          return;

        case 'PASSWORD_TOO_SIMPLE':
          setError('password', {
            type: 'tooSimple',
            message: i18n.t('password_too_simple') as string,
          });
          return;

        default:
          break;
      }

      setNotification({
        severity: 'error',
        title: i18n.t('error'),
        message: i18n.t('error_occured_try_later'),
      });
      setShowNotification(true);
    }
  };

  const password = watch('password');
  const passwordStrength = getPasswordStrength(password);

  return (
    <Root style={{ margin: '0 2dvh 2dvh 2dvh' }}>
      <NotificationToast
        open={showNotification}
        message={notification && notification.message}
        title={notification && notification.title}
        severity={notification && notification.severity}
        onClose={() => setShowNotification(false)}
      />
      <h2>{i18n.t('password_recovery')}</h2>
      <Label>{i18n.t('reset_password_form')}</Label>
      <FormContainer
        style={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'start',
          height: '100%',
        }}
      >
        <form noValidate autoComplete="off">
          <StyledDiv>
            <span>{i18n.t('insert_password')}</span>
            <StyledTextField
              fullWidth
              error={!!errors.password}
              {...register('password', {
                validate: (value) => {
                  if (value && value.length > MAX_PASSWORD_LENGTH) {
                    return i18n.t('password_too_long', { max: MAX_PASSWORD_LENGTH }) as ValidateResult;
                  }
                  const valid = getPasswordStrength(value).score >= 3;
                  if (!valid) {
                    return i18n.t('password_too_simple') as ValidateResult;
                  }
                  return valid;
                },
              })}
              helperText={(
                <div>
                  {(
                    password && (
                      <LinearProgress
                        style={{ backgroundColor: passwordStrength.color }}
                        value={100 - (passwordStrength.score + 1) * 25}
                        variant="determinate"
                      />
                    )
                  )}
                  {errors.password ? errors.password.message : ' '}
                </div>
              )}
              InputProps={{
                endAdornment: password ? (
                  <InputAdornment position="end">
                    <span style={{ color: passwordStrength.color }}>
                      {passwordStrength.message}
                    </span>
                  </InputAdornment>
                ) : null,
              }}
              name="password"
              type="password"
            />
          </StyledDiv>
          <StyledDiv>
            <span>{i18n.t('repeat_password')}</span>
            <StyledTextField
              fullWidth
              error={!!errors.confirmPassword}
              {...register('confirmPassword', {
                validate: (value) => {
                  const valid = getValues().password === value;
                  if (!valid) {
                    return i18n.t('passwords_do_not_coincide') as ValidateResult;
                  }
                  return valid;
                },
              })}
              helperText={
                (
                  <div>
                    {errors.confirmPassword ? errors.confirmPassword.message : ''}
                  </div>
                )
              }
              name="confirmPassword"
              type="password"
            />
          </StyledDiv>
          <ButtonsContainer>
            <StyledButton
              variant="outlined"
              color="secondary"
              $bgColor={theme.buttonColor}
              disabled={!submitButtonEnabled || Object.keys(errors).length > 0}
              onClick={handleSubmit(onSubmit)}
            >
              {i18n.t('save')}
            </StyledButton>
            {loading && <CircularProgress size={24} style={{ color: theme.loadingIconColor}} />}
          </ButtonsContainer>
        </form>
      </FormContainer>
    </Root>
  );
};

// @ts-ignore
export default withRouter(withStyles(style)(ResetPasswordForm));
